import { Box, Paper, IconButton, Tooltip, Typography } from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";

import { useEffect, useState } from "react";
import { getLeads, updateLeadStatus } from "../../api/api";
import { useNavigate } from "react-router-dom";

import DataTable from "../../components/datatable/DataTable";
import StatusChip from "../../components/datatable/StatusChip";
import FilterBar from "../../components/common/FilterBar";

import { GridColDef } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import ConfirmModal from "../../components/common/ConfirmModal";
import InputField from "../../components/common/InputField";

const Leads = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const [pendingModal, setPendingModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState("");
  const [pendingReason, setPendingReason] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 🔥 FETCH LEADS
  const fetchLeads = async () => {
    try {
      setLoading(true);

      const params: any = {};
      if (search) params.search = search;
      if (status) params.status = status;

      const res = await getLeads(params);
      setLeads(res.data?.data || []);
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to fetch leads",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, payload: any) => {
    try {
      await updateLeadStatus(id, payload);

      dispatch(
        showSnackbar({
          message: `Status updated successfully`,
          type: "success",
        }),
      );

      // if (status === "credit") {
      //   navigate(`/credit/${id}`);
      //   return;
      // }

      fetchLeads();
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || err.msg || "Status update failed",
          type: "error",
        }),
      );
    }
  };

  const handlePendingSubmit = async () => {
    try {
      if (!pendingReason.trim()) {
        dispatch(
          showSnackbar({
            message: "Pending reason required",
            type: "warning",
          }),
        );

        return;
      }

      await updateLeadStatus(selectedLead, {
        status: "pending",
        pendingReason,
      });

      dispatch(
        showSnackbar({
          message: "Marked as pending",
          type: "success",
        }),
      );

      setPendingModal(false);
      setPendingReason("");
      setSelectedLead("");

      fetchLeads();
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || err.msg || "Status update failed",

          type: "error",
        }),
      );
    }
  };

  // 🔥 DEBOUNCE
  useEffect(() => {
    const delay = setTimeout(fetchLeads, 400);
    return () => clearTimeout(delay);
  }, [search, status]);

  // 🔥 TABLE COLUMNS
  const columns: GridColDef[] = [
    {
      field: "leadCode",
      headerName: "Lead ID",
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 1,
    },
    {
      field: "productType",
      headerName: "Product",
      flex: 1,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "loanAmount",
      headerName: "Amount",
      flex: 1,
      renderCell: (params) => {
        const amt =
          params.row.loanAmount || params.row.amount || params.row.loan?.amount;

        return amt ? `₹ ${Number(amt).toLocaleString("en-IN")}` : "-";
      },
    },
    {
      field: "pendingReason",
      headerName: "Pending Reason",
      flex: 1.5,
      renderCell: (params) =>
        params.row?.pendingReason ? (
          <Tooltip title={params.row.pendingReason}>
            <Typography
              noWrap
              sx={{
                fontSize: 12,
                color: "#d97706",
                fontWeight: 500,
                maxWidth: 140,
              }}
            >
              {params.row.pendingReason}
            </Typography>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              py: 0.5,
            }}
          >
            {/* EDIT */}
            <Tooltip title="View Lead">
              <IconButton
                size="small"
                onClick={() => navigate(`/leads/view/${row._id}`)}
                sx={{
                  background: "#dbeafe",
                  color: "#2563eb",
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {(row.status === "pending" || row.status === "login") && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/leads/edit/${row._id}`)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* MARK PENDING */}
            {["login", "credit", "pending"].includes(row.status) && (
              <Tooltip title="Mark Pending">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedLead(row._id);
                    setPendingModal(true);
                  }}
                  sx={{
                    background: "#fff7ed",
                    color: "#ea580c",
                  }}
                >
                  ⏳
                </IconButton>
              </Tooltip>
            )}
            {/* MARK CREDIT */}
            {["login", "pending"].includes(row.status) && (
              <Tooltip title="Mark Credit">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleStatusChange(row._id, {
                      status: "credit",
                    })
                  }
                  sx={{
                    background: "#dcfce7",
                    color: "#16a34a",
                  }}
                >
                  ✔
                </IconButton>
              </Tooltip>
            )}

            {/* CREDIT SCREEN */}
            {row.status === "credit" && (
              <Tooltip title="Open Credit">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/credit/create/${row._id}`)}
                  sx={{
                    background: "#e0e7ff",
                    color: "#6366f1",
                  }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 1 }}>
      {/* FILTER */}
      <FilterBar
        title="Lead Management"
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        actionButtonText="+ Add Lead"
        onActionClick={() => navigate("/leads/create")}
      />

      {/* TABLE */}
      <Paper
        sx={{
          mt: 2,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <DataTable rows={leads} columns={columns} loading={loading} />
      </Paper>
      <ConfirmModal
        open={pendingModal}
        title="Pending Reason"
        confirmText="Mark Pending"
        cancelText="Cancel"
        onClose={() => {
          setPendingModal(false);
          setPendingReason("");
        }}
        onConfirm={handlePendingSubmit}
        description={
          <Box sx={{ mt: 1 }}>
            <InputField
              label="Reason"
              placeholder="Enter pending reason"
              value={pendingReason}
              onChange={(e: any) => setPendingReason(e.target.value)}
            />

            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "#64748b",
              }}
            >
              Example: RC Pending, FI Pending, Bank Statement Missing
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};

export default Leads;
